import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Store, Plug, RefreshCw, Zap, Tag, Package, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function HomeCentreIntegrationPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [locations, setLocations] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [syncingProducts, setSyncingProducts] = useState(false);
  const [syncingOrders, setSyncingOrders] = useState(false);
  const [ordersFromDate, setOrdersFromDate] = useState('');
  const [markupType, setMarkupType] = useState('none');
  const [markupValue, setMarkupValue] = useState(0);
  const [shipFee, setShipFee] = useState({ enabled: false, threshold: '', amount: '' });
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(false);

  const load = async () => {
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API}/homecentre/status`, auth()),
        axios.get(`${API}/enterprise/locations`, auth()),
      ]);
      setStatus(s.data);
      setMarkupType(s.data?.price_markup_type || 'none');
      setMarkupValue(s.data?.price_markup_value || 0);
      setShipFee({ enabled: !!s.data?.shipping_fee_enabled, threshold: s.data?.shipping_fee_threshold || '', amount: s.data?.shipping_fee_amount || '' });
      setLocations(l.data.locations || l.data || []);
    } catch (e) {
      toast.error('Failed to load Home Centre status');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const connect = async () => {
    if (!username.trim() || !password) { toast.error('Enter your Home Centre seller user id and password'); return; }
    setConnecting(true);
    try {
      await axios.post(`${API}/homecentre/connect`, { username: username.trim(), password }, auth());
      toast.success('Connected to Home Centre');
      setUsername(''); setPassword('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Connection failed');
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Home Centre? Stock sync and order polling will stop.')) return;
    try { await axios.post(`${API}/homecentre/disconnect`, {}, auth()); toast.success('Disconnected'); load(); }
    catch { toast.error('Failed to disconnect'); }
  };

  const saveSetting = async (patch) => {
    try {
      await axios.put(`${API}/homecentre/settings`, patch, auth());
      setStatus((s) => ({ ...s, ...patch }));
      toast.success('Settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };

  const saveMarkup = async () => {
    await saveSetting({
      price_markup_type: markupType, price_markup_value: Number(markupValue) || 0,
      shipping_fee_enabled: !!shipFee.enabled,
      shipping_fee_threshold: parseFloat(shipFee.threshold) || 0,
      shipping_fee_amount: parseFloat(shipFee.amount) || 0,
    });
    if (productSearch) loadProducts();
  };

  const syncProducts = async () => {
    setSyncingProducts(true);
    try {
      const r = await axios.post(`${API}/homecentre/sync-products`, {}, auth());
      toast.success(`Products synced — ${r.data.fetched} fetched, ${r.data.auto_mapped} auto-mapped to your SKUs`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Product sync failed'); }
    finally { setSyncingProducts(false); }
  };

  const syncStock = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/homecentre/sync-stock`, {}, auth());
      toast.success(`Stock synced — ${r.data.pushed} of ${r.data.total} updates pushed to Home Centre`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  const syncOrders = async () => {
    setSyncingOrders(true);
    try {
      const r = await axios.post(`${API}/homecentre/sync-orders`, ordersFromDate ? { from_date: ordersFromDate } : {}, auth());
      toast.success(r.data.message || 'Home Centre orders synced');
    } catch (e) { toast.error(e.response?.data?.detail || 'Order sync failed'); }
    finally { setSyncingOrders(false); }
  };

  const loadProducts = async () => {
    setLoadingProducts(true);
    try {
      const r = await axios.get(`${API}/homecentre/products`, { ...auth(), params: { search: productSearch || undefined } });
      setProducts(r.data.products || []);
    } catch (e) { toast.error('Could not load products'); }
    finally { setLoadingProducts(false); }
  };

  const pushPrice = async (p, price) => {
    try {
      await axios.post(`${API}/homecentre/push-price`, { sku: p.mapped_sku, ...(price != null ? { price } : {}) }, auth());
      toast.success(`Price pushed for ${p.mapped_sku}`);
      loadProducts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not push price'); }
  };

  if (loading) return <div className="p-8 text-wt-text-muted" data-testid="homecentre-loading">Loading…</div>;
  const connected = status?.connected;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="homecentre-page">
      <button onClick={() => navigate('/integrations')} className="flex items-center gap-2 text-sm text-wt-text-muted hover:text-wt-text-primary" data-testid="homecentre-back-btn">
        <ArrowLeft className="h-4 w-4" /> Back to Integrations
      </button>

      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-orange-500 to-rose-600 flex items-center justify-center">
          <Store className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-wt-text-primary">Home Centre</h1>
          <p className="text-sm text-wt-text-muted">Landmark Group (Vin eRetail) — ingest orders, sync stock & price, push AWB / invoice / label.</p>
        </div>
        {connected && <Badge className="ml-auto bg-green-500/15 text-green-500 border border-green-500/30" data-testid="homecentre-connected-badge">Connected</Badge>}
      </div>

      {/* Connection */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl border border-wt-border bg-wt-surface p-5 space-y-4">
        <div className="flex items-center gap-2"><Plug className="h-4 w-4 text-wt-gold" /><h2 className="font-semibold text-wt-text-primary">Connection</h2></div>
        {!connected ? (
          <div className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Seller User ID</Label><Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="e.g. 2349599" data-testid="homecentre-username-input" /></div>
              <div><Label>Password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="homecentre-password-input" /></div>
            </div>
            <p className="text-xs text-wt-text-muted">Uses your Home Centre seller-panel login to fetch a permanent API key. No separate keys needed.</p>
            <Button onClick={connect} disabled={connecting} className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="homecentre-connect-btn">
              {connecting ? 'Connecting…' : 'Connect Home Centre'}
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm text-wt-text-secondary">
              <p><b className="text-wt-text-primary">Seller {status.api_owner}</b></p>
              <p className="text-wt-text-muted">{status.listings_count} listings · {status.unmapped_count} unmapped</p>
            </div>
            <Button variant="outline" onClick={disconnect} className="border-red-500/30 text-red-400" data-testid="homecentre-disconnect-btn">Disconnect</Button>
          </div>
        )}
      </motion.div>

      {connected && (
        <>
          {/* Automation */}
          <div className="rounded-xl border border-wt-border bg-wt-surface p-5 space-y-4">
            <div className="flex items-center gap-2"><Zap className="h-4 w-4 text-wt-gold" /><h2 className="font-semibold text-wt-text-primary">Automation</h2></div>
            <div className="flex items-center justify-between">
              <div><p className="text-sm text-wt-text-primary">Auto-sync stock & price</p><p className="text-xs text-wt-text-muted">Push updates automatically whenever stock changes (every 2 min) plus an hourly full reconcile. Turn off to sync only with the button below.</p></div>
              <Switch checked={!!status.auto_sync} onCheckedChange={(v) => saveSetting({ auto_sync: v })} data-testid="homecentre-autosync-switch" />
            </div>
            <div className="border-t border-wt-border pt-4 flex items-center justify-between">
              <div><p className="text-sm text-wt-text-primary">Sync orders to Shopify</p><p className="text-xs text-wt-text-muted">Also create each Home Centre order on Shopify. The Home Centre order number stays the main number.</p></div>
              <Switch checked={!!status.sync_to_shopify} onCheckedChange={(v) => saveSetting({ sync_to_shopify: v })} data-testid="homecentre-shopify-switch" />
            </div>
          </div>

          {/* Pricing */}
          <div className="rounded-xl border border-wt-border bg-wt-surface p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Tag className="h-4 w-4 text-wt-gold" /><h2 className="font-semibold text-wt-text-primary">Price Sync</h2></div>
              <Switch checked={!!status.price_sync_enabled} onCheckedChange={(v) => saveSetting({ price_sync_enabled: v })} data-testid="homecentre-pricesync-switch" />
            </div>
            <p className="text-xs text-wt-text-muted">When on, your Shopify selling price is pushed to Home Centre on each stock sync, adjusted by the markup below. Use a negative value to sell cheaper than Shopify.</p>
            <div className="grid sm:grid-cols-3 gap-3 items-end">
              <div>
                <Label>Adjustment</Label>
                <Select value={markupType} onValueChange={setMarkupType}>
                  <SelectTrigger data-testid="homecentre-markup-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Same as Shopify</SelectItem>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed amount</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Value {markupType === 'percentage' ? '(%)' : markupType === 'fixed' ? '(AED)' : ''}</Label>
                <Input type="number" step="0.01" value={markupValue} disabled={markupType === 'none'}
                  onChange={(e) => setMarkupValue(e.target.value)} data-testid="homecentre-markup-value" />
              </div>
              <Button onClick={saveMarkup} className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="homecentre-save-markup-btn">Save Pricing</Button>
            </div>
            <div className="border-t border-wt-border pt-4 space-y-2">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-wt-text-primary">Add shipping fee to low-priced products</p>
                  <p className="text-xs text-wt-text-muted">If the price <b>after markup</b> is below the threshold, a fixed shipping amount is added on top. Save with the button above.</p>
                </div>
                <Switch checked={shipFee.enabled} onCheckedChange={(v) => setShipFee((f) => ({ ...f, enabled: v }))} data-testid="homecentre-shipfee-switch" />
              </div>
              {shipFee.enabled && (
                <div className="flex items-center gap-2 text-sm text-wt-text-muted flex-wrap">
                  <span>If price below AED</span>
                  <Input type="number" min="0" step="0.01" className="w-24" value={shipFee.threshold}
                    onChange={(e) => setShipFee((f) => ({ ...f, threshold: e.target.value }))} placeholder="50" data-testid="homecentre-shipfee-threshold-input" />
                  <span>add AED</span>
                  <Input type="number" min="0" step="0.01" className="w-24" value={shipFee.amount}
                    onChange={(e) => setShipFee((f) => ({ ...f, amount: e.target.value }))} placeholder="35" data-testid="homecentre-shipfee-amount-input" />
                </div>
              )}
            </div>
          </div>

          {/* Stock sync */}
          <div className="rounded-xl border border-wt-border bg-wt-surface p-5 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2"><Package className="h-4 w-4 text-wt-gold" /><h2 className="font-semibold text-wt-text-primary">Catalog & Stock</h2></div>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={syncProducts} disabled={syncingProducts} className="border-wt-border text-wt-text-primary" data-testid="homecentre-sync-products-btn">
                  <RefreshCw className={`h-4 w-4 mr-1 ${syncingProducts ? 'animate-spin' : ''}`} /> Sync Products & Map SKUs
                </Button>
                <Button size="sm" onClick={syncStock} disabled={syncing} className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="homecentre-sync-stock-btn">
                  <RefreshCw className={`h-4 w-4 mr-1 ${syncing ? 'animate-spin' : ''}`} /> Sync Stock Now
                </Button>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3 items-end">
              <div>
                <Label>Stock source warehouse</Label>
                <Select value={status.stock_location_id || 'all'} onValueChange={(v) => saveSetting({ stock_location_id: v === 'all' ? null : v })}>
                  <SelectTrigger data-testid="homecentre-location-select"><SelectValue placeholder="All warehouses" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All warehouses (combined)</SelectItem>
                    {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Stock buffer (keep in reserve)</Label>
                <Input type="number" min="0" defaultValue={status.stock_buffer || 0}
                  onBlur={(e) => saveSetting({ stock_buffer: Number(e.target.value) || 0 })} data-testid="homecentre-buffer-input" />
              </div>
              <p className="text-xs text-wt-text-muted">Virtual (sell-without-stock) quantity is added on top of real available stock, same as your other channels.</p>
            </div>
            {status.last_sync && <p className="text-xs text-wt-text-muted">Last sync: {new Date(status.last_sync.created_at).toLocaleString()} ({status.last_sync.trigger})</p>}
          </div>

          {/* Orders */}
          <div className="rounded-xl border border-wt-border bg-wt-surface p-5">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div className="flex items-center gap-2"><Store className="h-4 w-4 text-wt-gold" /><h2 className="font-semibold text-wt-text-primary">Orders</h2></div>              <div className="flex items-center gap-2">
                <input type="date" value={ordersFromDate} onChange={(e) => setOrdersFromDate(e.target.value)}
                  className="h-9 px-2 rounded-md bg-wt-bg border border-wt-border text-wt-text-primary text-xs" data-testid="homecentre-orders-from-date" />
                <Button size="sm" variant="outline" onClick={syncOrders} disabled={syncingOrders} className="border-wt-border text-wt-text-primary" data-testid="homecentre-sync-orders-btn">
                  <RefreshCw className={`h-4 w-4 mr-1 ${syncingOrders ? 'animate-spin' : ''}`} /> {syncingOrders ? 'Syncing…' : 'Sync Orders Now'}
                </Button>
              </div>
            </div>
            <p className="text-sm text-wt-text-muted">New Home Centre orders appear in <button onClick={() => navigate('/orders')} className="text-wt-gold underline" data-testid="homecentre-goto-orders">Orders</button> (auto-polled every 5 min). Mark an order Ready to Ship to push the invoice & AWB back to Home Centre; download the shipping label from the order.</p>
          </div>

          {/* Pending delivery confirmations card removed — order processors don't
              access this page; the reminder lives on the Home Centre order itself. */}

          {/* Per-product price */}
          <div className="rounded-xl border border-wt-border bg-wt-surface p-5 space-y-3">
            <div className="flex items-center gap-2"><Tag className="h-4 w-4 text-wt-gold" /><h2 className="font-semibold text-wt-text-primary">Update a Product's Price</h2></div>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-wt-text-muted" />
                <Input className="pl-9" placeholder="Search by your SKU, Home Centre SKU or name…" value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') loadProducts(); }}
                  data-testid="homecentre-product-search" />
              </div>
              <Button variant="outline" onClick={loadProducts} disabled={loadingProducts} className="border-wt-border text-wt-text-primary" data-testid="homecentre-product-search-btn">Search</Button>
            </div>
            <div className="space-y-2" data-testid="homecentre-products-list">
              {products.map((p) => (
                <div key={p.channel_sku} className="flex items-center justify-between gap-3 rounded-lg border border-wt-border p-3" data-testid={`homecentre-product-${p.channel_sku}`}>
                  <div className="text-sm min-w-0">
                    <p className="text-wt-text-primary font-medium truncate">{p.channel_product_title || p.mapped_sku}</p>
                    <p className="text-wt-text-muted text-xs">{p.mapped_sku} · HC {p.channel_sku} · Shopify {p.shopify_price != null ? `AED ${p.shopify_price}` : '—'}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Input type="number" step="0.01" defaultValue={p.homecentre_price ?? ''} className="w-28 h-8 text-right"
                      data-testid={`homecentre-price-input-${p.channel_sku}`}
                      onKeyDown={(e) => { if (e.key === 'Enter') pushPrice(p, Number(e.target.value)); }}
                      ref={(el) => { p._el = el; }} />
                    <Button size="sm" className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid={`homecentre-push-price-${p.channel_sku}`}
                      onClick={() => pushPrice(p, Number(p._el?.value))}>Push</Button>
                  </div>
                </div>
              ))}
              {products.length === 0 && <p className="text-sm text-wt-text-muted">Search to find a mapped product and push its price. Leave the field as-is to use your markup rule, or type a custom price.</p>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
